/**
 * TutorArchivedActivitiesScreen — la route `archived-activites` pour un tuteur (« Activités archivées », le
 * menu de l'onglet Activités de la fiche d'un élève).
 *
 * La page, les pages d'archives et la restauration multiple sont celles du parent
 * (`useArchivedActivitiesData`, `archivedActivitiesViewModel`, `useArchivedActivitiesState`) : mêmes appels
 * que `ArchivedActivitiesScreen.js` (`getArchivedActivities` par vingt, `with_logs`, puis
 * `restoreArchivedActivities`). Le tuteur y a sa fiche (`useTutorActivityDetails`, entrée « archives ») et,
 * après une restauration, l'élève se relit (`getStudent`) : sa liste à faire retrouve l'activité
 * (`ArchivedActivitiesScreen.js:131-133`).
 */
import React, { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { studentsService } from "../../../../../../data/services/studentsService"
import { ArchivedActivitiesPage, ConfirmDialog, Snackbar } from "../../../../../../design-system"
import { TutorActivityDetailsModal } from "../../../../../../design-system/tutor"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import { type ApiTutorActivity } from "../../../../adapters/tutor/Students/tutorStudentActivityAdapter"
import { archivedActivitiesViewModel } from "../../../../view-models/parent/Activities/archivedActivitiesViewModel"
import { useArchivedActivitiesData } from "../../parent/Activities/useArchivedActivitiesData"
import { useArchivedActivitiesState } from "../../parent/Activities/useArchivedActivitiesState"
import { type TutorSpaceScreenProps } from "../tutorSpaceRouteTypes"
import { useTutorActivityDetails } from "./useTutorActivityDetails"

export function TutorArchivedActivitiesScreen(props: TutorSpaceScreenProps) {
    const routeStudentId = (props.route?.params as { studentId?: string | number } | undefined)?.studentId
    const dispatch = useDispatch()
    const { studentId, raw, refresh, loadMore, hasMore } = useArchivedActivitiesData(routeStudentId)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => archivedActivitiesViewModel(state, raw))

    const afterRestore = useCallback(() => {
        refresh()
        if (studentId != null) dispatch(apiRequest(studentsService.getStudent, { id: studentId }) as never)
    }, [dispatch, refresh, studentId])

    const details = useTutorActivityDetails({ studentId })
    const openDetails = useCallback(
        (activity: ApiActivity) => {
            if (activity.id == null) return
            details.open(activity.id, {
                fromArchived: true,
                seed: activity as ApiTutorActivity,
                // La fiche relit déjà l'élève après « Restaurer » : la liste seule.
                onRestored: refresh,
            })
        },
        [details, refresh]
    )
    const { selection, restoreConfirm, restoring, snackbar, actions } = useArchivedActivitiesState({
        studentId,
        activities: vm.activities,
        refresh: afterRestore,
        raw,
        onOpenActivity: openDetails,
    })

    // Une seule fente d'accusé : celui de la fiche d'abord (le plus récent, elle est modale quand elle parle).
    const notice = details.notice.visible
        ? details.notice
        : {
              visible: snackbar != null,
              message: snackbar?.message ?? "",
              tone: snackbar?.tone,
              onDismiss: actions.dismissSnackbar,
          }

    return (
        <>
            <ArchivedActivitiesPage
                activities={vm.activities}
                loading={vm.loading}
                refreshing={vm.refreshing}
                loadingMore={vm.loadingMore}
                hasMore={hasMore}
                error={vm.error}
                onRetry={refresh}
                onRefresh={refresh}
                onLoadMore={loadMore}
                onClose={actions.close}
                onOpenActivity={actions.openActivity}
                selectionMode={selection.selectionMode}
                selectedIds={selection.selectedIds}
                onStartSelection={actions.startSelection}
                onToggleSelect={actions.toggleSelect}
                onToggleSelectAll={actions.toggleSelectAll}
                onCancelSelection={actions.cancelSelection}
                onRestoreSelected={actions.askRestore}
                restoring={restoring}
            />
            <ConfirmDialog
                visible={restoreConfirm != null}
                title={restoreConfirm?.title ?? ""}
                message={restoreConfirm?.message}
                confirmLabel="Restaurer"
                cancelLabel="Annuler"
                danger={false}
                onConfirm={actions.confirmRestore}
                onCancel={actions.cancelRestore}
            />
            <TutorActivityDetailsModal {...details.modal} />
            <Snackbar {...notice} />
        </>
    )
}

export default TutorArchivedActivitiesScreen
